<template>
    <setting-modal
        v-if="setting"
        :setting="setting"
        :open="modal"
        @fetch="
            modal = false;
            fetch();
        "
        @close="modal = false"
    />

    <div
        class="mb-3 overflow-hidden rounded-xl bg-slate-50 p-3 dark:bg-slate-800/25"
    >
        <h3 class="mt-1 text-lg font-medium leading-6 dark:text-white">
            Indicado por
        </h3>
        <p
            v-if="referredByLoading"
            class="py-4 text-sm text-gray-600 dark:text-gray-200"
        >
            Carregando...
        </p>
        <p
            v-else-if="referredByError"
            class="py-4 text-sm text-gray-600 dark:text-gray-200"
        >
            Não foi possível carregar a indicação desta conta.
        </p>
        <p
            v-else-if="!referredBy"
            class="py-4 text-sm text-gray-600 dark:text-gray-200"
        >
            Esta conta não foi indicada.
        </p>
        <div v-else class="grid grid-cols-1 gap-6 py-4 text-sm sm:grid-cols-2">
            <div class="flex flex-col">
                <span class="font-bold text-gray-600 dark:text-gray-400">
                    INDICADOR
                </span>
                <router-link
                    v-if="referredBy.referring"
                    class="text-indigo-500 hover:underline"
                    :to="{
                        name: 'user.referrals',
                        params: { user_id: referredBy.referring.id },
                    }"
                >
                    {{ referredBy.referring.name }} ({{
                        referredBy.referring.email
                    }})
                </router-link>
                <span v-else class="text-gray-600 dark:text-gray-200">
                    Não encontrado
                </span>
            </div>
            <div class="flex flex-col">
                <span class="font-bold text-gray-600 dark:text-gray-400">
                    STATUS
                </span>
                <span class="text-gray-600 dark:text-gray-200">
                    <referral-status :status="referredBy.status" />
                    <span v-if="referredBy.deleted_at" class="ml-2">
                        Vínculo apagado em {{ referredBy.deleted_at }}
                    </span>
                </span>
            </div>
            <div class="flex flex-col">
                <span class="font-bold text-gray-600 dark:text-gray-400">
                    COMO
                </span>
                <span class="text-gray-600 dark:text-gray-200">
                    {{ sourceLabel }}
                </span>
            </div>
            <div class="flex flex-col">
                <span class="font-bold text-gray-600 dark:text-gray-400">
                    REGRA APLICADA
                </span>
                <span class="text-gray-600 dark:text-gray-200">
                    {{ ruleLabel }}
                </span>
            </div>
            <div class="flex flex-col">
                <span class="font-bold text-gray-600 dark:text-gray-400">
                    ONDE
                </span>
                <span class="text-gray-600 dark:text-gray-200">
                    {{ placeLabel }}
                </span>
            </div>
            <div class="flex flex-col">
                <span class="font-bold text-gray-600 dark:text-gray-400">
                    INDICADO DESDE
                </span>
                <span class="text-gray-600 dark:text-gray-200">
                    {{ formatDate(referredSince) }}
                </span>
            </div>
            <div class="flex flex-col">
                <span class="font-bold text-gray-600 dark:text-gray-400">
                    VÍNCULO E COMISSÃO DESDE
                </span>
                <span class="text-gray-600 dark:text-gray-200">
                    {{ referredBy.created_at }}
                </span>
            </div>
            <div class="flex flex-col">
                <span class="font-bold text-gray-600 dark:text-gray-400">
                    EXPIRA EM
                </span>
                <span class="text-gray-600 dark:text-gray-200">
                    {{ referredBy.expiration_date }}
                </span>
            </div>
            <div v-if="discarded.length" class="flex flex-col sm:col-span-2">
                <span class="font-bold text-gray-600 dark:text-gray-400">
                    DESCARTADOS
                </span>
                <ul class="text-gray-600 dark:text-gray-200">
                    <li v-for="(item, index) in discarded" :key="index">
                        {{ item.pit }}: {{ reasonLabel(item.reason) }}
                        <template v-if="item.clicked_at">
                            (clique em {{ formatDate(item.clicked_at) }})
                        </template>
                        <template v-if="item.order_hash">
                            (pedido {{ item.order_hash }})
                        </template>
                    </li>
                </ul>
            </div>
            <div
                v-if="referredBy.observation"
                class="flex flex-col sm:col-span-2"
            >
                <span class="font-bold text-gray-600 dark:text-gray-400">
                    OBSERVAÇÃO DO COMPLIANCE
                </span>
                <span class="text-gray-600 dark:text-gray-200">
                    {{ referredBy.observation }}
                </span>
            </div>
        </div>
    </div>

    <div
        class="overflow-hidden rounded-xl bg-slate-50 p-3 dark:bg-slate-800/25"
    >
        <div class="w-full">
            <div class="flex justify-between">
                <h3 class="mt-1 text-lg font-medium leading-6 dark:text-white">
                    Programa de Indicações
                </h3>
                <button
                    v-if="hasPermission('update.user_referrals')"
                    type="button"
                    class="mt-3 inline-flex w-full items-center justify-center rounded-md border border-indigo-500 bg-indigo-500 px-4 py-1 text-base font-medium text-white shadow-sm hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
                    @click="modal = true"
                >
                    <font-awesome-icon
                        icon="fa-solid fa-cog"
                        class="mr-1 dark:text-white"
                        aria-hidden="true"
                    />
                    Configurações
                </button>
            </div>
            <div class="py-4">
                <div>
                    <div
                        class="my-4 flex flex-col justify-between text-sm text-white"
                    >
                        <div class="my-4 flex flex-col">
                            <span
                                class="text-sm font-bold font-medium text-gray-600 dark:text-gray-400"
                            >
                                DESCRIÇÃO
                            </span>
                            <span class="text-gray-600 dark:text-gray-200">
                                {{ setting.description ?? "Não informado" }}
                            </span>
                        </div>
                        <div class="my-4 flex flex-col">
                            <span
                                class="text-sm font-medium text-gray-600 dark:text-gray-400"
                                ><b>COMISSÃO PADRÃO</b></span
                            >
                            <span class="text-gray-600 dark:text-gray-200">
                                {{ setting.commission }}%</span
                            >
                        </div>
                        <div class="my-4 flex flex-col">
                            <span
                                class="text-sm font-medium text-gray-600 dark:text-gray-400"
                                ><b>STATUS</b></span
                            >
                            <span class="pt-3 text-gray-600 dark:text-gray-200">
                                <referral-status :status="setting.status" />
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import ReferralStatus from "../../components/layouts/ReferralStatus.vue";
import SettingModal from "../../components/referrals/SettingModal.vue";
import HelperMixin from "../../mixins/HelperMixin.vue";
import PermissionMixin from "../../mixins/PermissionMixin.vue";

const SOURCE_LABELS = {
    signup_url: "Cadastro com o código na URL do link de indicação",
    signup_cookie:
        "Cadastro com o código do primeiro clique no link de indicação",
    checkout_purchase:
        "Conta criada na compra pelo link da oferta com o código do produtor",
    referral_link: "Clique no link de indicação antes de a conta existir",
};

const RULE_LABELS = {
    url: "Código da URL do cadastro",
    first_click: "Primeiro clique",
    purchase: "Compra que criou a conta",
    purchase_wins: "Compra que criou a conta, que ganha do clique no link",
};

const REASON_LABELS = {
    not_first_click: "clique depois do primeiro",
    inactive_program: "código sem programa de indicação ativo",
    purchase_wins: "perdeu para a compra com o código do produtor",
    not_offer_producer: "código não é do produtor da oferta",
    self_referral: "autoindicação",
    click_after_account_creation: "clique depois de a conta existir",
    click_date_missing: "clique sem data",
    click_expired: "clique com mais de 30 dias",
    purchase_not_paid: "compra sem pagamento aprovado",
};

const ORIGIN_LABELS = {
    register: "Formulário de cadastro da dash",
    website: "Formulário de cadastro do site",
};

export default {
    props: ["user"],

    components: {
        SettingModal,
        ReferralStatus,
    },

    mixins: [PermissionMixin, HelperMixin],
    data() {
        return {
            setting: false,
            modal: false,
            loading: true,
            referrals: [],
            referredBy: null,
            referredByLoading: true,
            referredByError: false,
        };
    },

    computed: {
        referralDetails() {
            return this.referredBy?.source_details ?? {};
        },
        discarded() {
            return this.referralDetails.discarded ?? [];
        },
        sourceLabel() {
            return (
                SOURCE_LABELS[this.referredBy?.source] ??
                "Origem não registrada (vínculo anterior ao registro de origem)"
            );
        },
        ruleLabel() {
            return RULE_LABELS[this.referralDetails.rule] ?? "-";
        },
        referredSince() {
            return (
                this.referralDetails.clicked_at ??
                this.referralDetails.purchased_at ??
                this.referralDetails.account_created_at ??
                null
            );
        },
        placeLabel() {
            const details = this.referralDetails;

            if (this.referredBy?.source === "checkout_purchase") {
                return `Pedido ${details.order_hash}, produto ${details.product_id}`;
            }

            if (this.referredBy?.source === "referral_link") {
                return `Página ${details.click_path ?? "-"} da dash`;
            }

            if (details.origin) {
                return (
                    ORIGIN_LABELS[details.origin] ??
                    `Cadastro com origem ${details.origin}`
                );
            }

            return "-";
        },
    },

    mounted() {
        this.fetch();
        this.fetchReferredBy();
    },

    openModal() {
        this.edit_referral_modal = true;
    },

    closeModal() {
        this.edit_referral_modal = false;
    },

    closeAndFetchModal() {
        this.edit_referral_modal = false;
        this.fetch();
    },

    methods: {
        fetch() {
            this.loading = true;
            axios
                .get(`/api/user/${this.user.id}/referrals`)
                .then(({ data }) => {
                    this.setting = data;
                    this.loading = false;
                })
                .catch((error) => {
                    this.loading = false;
                });
        },
        fetchReferredBy() {
            this.referredByLoading = true;
            this.referredByError = false;
            axios
                .get(`/api/user/${this.user.id}/referred-by`)
                .then(({ data }) => {
                    this.referredBy = data.data;
                })
                .catch(() => {
                    this.referredByError = true;
                })
                .finally(() => {
                    this.referredByLoading = false;
                });
        },
        reasonLabel(reason) {
            return REASON_LABELS[reason] ?? reason;
        },
    },
};
</script>
